import React, { useEffect, useState, useRef } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Row,
} from "reactstrap"
import { useFormik } from "formik"
import { validateBeforeConfirm } from "helpers/validation_helper"
import { useDispatch, useSelector } from "react-redux"
import {
    getMerchantVariables,
    createMerchantVariables,
    updateMerchantVariables,
    getReconciliationFrequencies,
} from "store/merchants/actions"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import TextInput from "components/Common/TextInput"
import SelectInput from "components/Common/SelectInput"
import CheckboxInput from "components/Common/CheckboxInput"
import CButton from "components/Common/CButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import * as url from "../../helpers/url_helper"

export default function MerchantVariablesForm({
    match,
    onTabCompletion,
    activeTab,
    tabId,
    createdMerchantID,
    page,
}) {
    const [readOnly, setReadOnly] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [merchantVariables, setMerchantVariables] = useState(null)
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const previousError = useRef(null)

    const detailID = match?.params?.id || createdMerchantID

    const dispatch = useDispatch()
    const { variablesError, variablesSuccess, reconciliationFrequencies, fetchedMerchantVariables } = useSelector(state => ({
        variablesError: state.merchants.variablesError?.message || state.merchants.variablesError?.description,
        variablesSuccess: state.merchants.variablesSuccess,
        reconciliationFrequencies: state.merchants.reconciliationFrequencies || [],
        fetchedMerchantVariables: state.merchants.merchantVariables,
    }))

    // Reconciliation frequency options - Filter out fortnightly always; filter biweekly and daily in prod
    const isProd = window.location.href.includes("prod")
    const reconciliationOptions = reconciliationFrequencies
        .filter(freq => {
            const frequency = freq.reconciliationFrequency?.toLowerCase()
            if (frequency === "fortnightly") return false
            if (isProd && (frequency === "biweekly" || frequency === "daily")) return false
            return true
        })
        .map(freq => ({
            value: freq.reconciliationFrequency?.toLowerCase(),
            label: freq.reconciliationFrequency?.charAt(0).toUpperCase() + freq.reconciliationFrequency?.slice(1).toLowerCase()
        }))

    const getMonthDayOptions = (frequency, useDefaultDates) => {
        if (useDefaultDates || frequency === "daily") return []

        let maxDays = 28
        switch (frequency) {
            case "monthly":
                maxDays = 28
                break
            case "fortnightly":
                maxDays = 14
                break
            case "weekly":
                maxDays = 7
                break
            case "biweekly":
                maxDays = 4
                break
            default:
                maxDays = 28
        }

        return Array.from({ length: maxDays }, (_, i) => ({
            value: (i + 1).toString(),
            label: (i + 1).toString(),
        }))
    }

    const getPercentageRange = (frequency) => {
        const frequencyData = reconciliationFrequencies.find(
            freq => freq.reconciliationFrequency?.toLowerCase() === (frequency?.toLowerCase?.() || frequency?.value?.toLowerCase())
        )
        if (!frequencyData || !frequencyData.percentageRange) {
            return { min: 0, max: 5 }
        }

        const [min, max] = frequencyData.percentageRange.split('-').map(num => parseFloat(num))
        return { min, max }
    }

    useEffect(() => {
        if (merchantVariables) {
            setReadOnly(true)
        } else if (page === "detail") {
            // In detail mode with no data, start in readonly state (showing add button)
            setReadOnly(true)
        }
    }, [merchantVariables, page])

    useEffect(() => {
        if (reconciliationFrequencies.length === 0) {
            dispatch(getReconciliationFrequencies())
        }
    }, [])

    // Fetch merchant variables only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (detailID && isTabActive) {
            dispatch(
                getMerchantVariables({
                    headers: {
                        "input-parameters": { merchantID: detailID },
                    },
                })
            )
        }
    }, [detailID, dispatch, activeTab, tabId])

    // Handle fetched merchant variables data
    useEffect(() => {
        if (fetchedMerchantVariables && detailID) {
            setMerchantVariables(fetchedMerchantVariables[0])
            setReadOnly(true)
        }
    }, [fetchedMerchantVariables, detailID])

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            merchantID: detailID || "",
            reconciliationFrequency: merchantVariables?.reconciliationFrequency?.toLowerCase() || "weekly",
            paymentProcessingFeePercent: merchantVariables?.paymentProcessingFeePercent || "",
            payoutProcessingFeePercent: merchantVariables?.payoutProcessingFeePercent || "",
            useDefaultDates: true,
            monthDay: (merchantVariables?.monthDays || merchantVariables?.monthDay)?.split(',')[0] || "",
            settlementThreshold: merchantVariables?.settlementThreshold || "",
        },
        validationSchema: Yup.object({
            reconciliationFrequency: Yup.string().required("Please Select Reconciliation Frequency"),

            paymentProcessingFeePercent: Yup.string()
                .required("Please Enter Payment Processing Fee Percent")
                .matches(/^\d+(\.\d{1,2})?$/, "Please enter a valid number")
                .test('range', 'Payment Processing Fee Percent must be between 0 and 5', function (value) {
                    const num = parseFloat(value)
                    return num >= 0 && num <= 5
                }),

            payoutProcessingFeePercent: Yup.string()
                .required("Please Enter Payout Processing Fee Percent")
                .matches(/^\d+(\.\d{1,2})?$/, "Please enter a valid number")
                .test('range', function (value) {
                    const frequency = this.parent.reconciliationFrequency
                    const { min, max } = getPercentageRange(frequency)
                    const num = parseFloat(value)
                    if (isNaN(num) || num < min || num > max) {
                        return this.createError({
                            message: `Payout Processing Fee Percent must be between ${min} and ${max} for ${frequency || 'selected'} frequency`
                        })
                    }
                    return true
                }),

            useDefaultDates: Yup.boolean().required(),

            monthDay: Yup.mixed().when(
                ["reconciliationFrequency", "useDefaultDates"],
                {
                    is: (frequency, useDefault) => !useDefault && frequency !== "daily",
                    then: schema => schema.required("Please Select Date"),
                    otherwise: schema => schema.notRequired(),
                }
            ),

            settlementThreshold: Yup.string()
                .test('positive', 'Settlement Threshold must be greater than zero', function (value) {
                    if (!value || value === '') return true // Optional field
                    const num = parseFloat(value)
                    return !isNaN(num) && num > 0
                })
                .matches(/^\d*(\.\d{1,2})?$/, "Please enter a valid number"),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Handle form submission
    const handleConfirmedSubmit = async () => {
        setIsSubmitting(true)
        try {
            const values = validation.values

            const payload = {
                merchantID: values.merchantID,
                reconciliationFrequency: values.reconciliationFrequency,
                paymentProcessingFeePercent: values.paymentProcessingFeePercent,
                payoutProcessingFeePercent: values.payoutProcessingFeePercent,
                useDefaultDates: values.useDefaultDates,
            }

            if (values.settlementThreshold && values.settlementThreshold !== '') {
                payload.settlementThreshold = values.settlementThreshold
            }

            if (!values.useDefaultDates && values.reconciliationFrequency !== "daily" && values.monthDay) {
                payload.monthDay = typeof values.monthDay === 'object' ? values.monthDay.value : values.monthDay
            }

            if (merchantVariables) {
                dispatch(updateMerchantVariables(payload, {}))
            } else {
                dispatch(createMerchantVariables(payload, {}))
            }

        } catch (error) {
            console.error("Form submission error:", error)
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        if (variablesSuccess && isSubmitting) {
            setReadOnly(true)
            setMerchantVariables(variablesSuccess)
            if (onTabCompletion) {
                onTabCompletion(activeTab)
            }
            setIsSubmitting(false)
        }
    }, [variablesSuccess, isSubmitting, onTabCompletion, activeTab])

    useEffect(() => {
        if (variablesError) {
            if (isSubmitting) {
                setIsSubmitting(false)
            }
            previousError.current = variablesError
        }
    }, [variablesError, isSubmitting])

    const monthDayOptions = getMonthDayOptions(
        validation.values.reconciliationFrequency,
        validation.values.useDefaultDates
    )

    useEffect(() => {
        const currentFreq = validation.values.reconciliationFrequency
        const useDefault = validation.values.useDefaultDates === true

        if (useDefault || currentFreq === "daily") {
            validation.setFieldValue("monthDay", "")
        } else {
            const maxDays = currentFreq === "monthly" ? 28 :
                currentFreq === "fortnightly" ? 14 :
                    currentFreq === "weekly" ? 7 :
                        currentFreq === "biweekly" ? 4 : 28

            if (validation.values.monthDay && validation.values.monthDay > maxDays) {
                validation.setFieldValue("monthDay", "")
            }
        }
    }, [validation.values.reconciliationFrequency, validation.values.useDefaultDates])

    // Determine if checkbox should be disabled
    const isCheckboxDisabled = readOnly || validation.values.reconciliationFrequency === "daily"

    // Determine if start day select should be disabled
    const isStartDayDisabled = readOnly ||
        validation.values.useDefaultDates === true ||
        validation.values.reconciliationFrequency === "daily"

    // Helper function to check if monthDay has a meaningful value
    const hasMonthDayValue = () => {
        const monthDay = validation.values.monthDay
        return monthDay && monthDay !== "" && monthDay !== null && monthDay !== undefined
    }

    // Helper function to check if useDefaultDates is true
    const hasUseDefaultDatesValue = () => {
        return validation.values.useDefaultDates === true
    }

    const getReconciliationDaysText = () => {
        const frequency = validation.values.reconciliationFrequency
        const useDefault = validation.values.useDefaultDates === true
        const selectedDay = validation.values.monthDay

        if (useDefault) {
            if (frequency === "monthly" || frequency === "weekly" || frequency === "fortnightly" || frequency === "biweekly") {
                const defaultFrequency = reconciliationFrequencies.find(
                    rf => rf.reconciliationFrequency?.toLowerCase() === frequency
                )
                if (defaultFrequency && defaultFrequency.monthDays) {
                    return `${defaultFrequency.monthDays}`
                }
            }
            return ""
        }

        if (!selectedDay || (frequency !== "monthly" && frequency !== "weekly" && frequency !== "fortnightly" && frequency !== "biweekly")) {
            return ""
        }

        const dayValue = typeof selectedDay === 'object' ? selectedDay.value : selectedDay
        const startDay = parseInt(dayValue)

        if (isNaN(startDay)) {
            return ""
        }

        const days = []

        if (frequency === "biweekly") {
            // logic for biweekly
            let currentDay = startDay
            let increment = 3

            while (currentDay <= 28) {
                days.push(currentDay)
                currentDay += increment
                increment = increment === 3 ? 4 : 3
            }
        } else {
            // logic for other frequencies
            const increment = frequency === "weekly" ? 7 :
                frequency === "fortnightly" ? 14 : 0

            for (let day = startDay; day <= 28; day += increment) {
                days.push(day)
            }
        }

        return days.length > 0 ? days.join(', ') : ""
    }

    useEffect(() => {
        if (validation.values.useDefaultDates === true) {
            validation.setFieldValue("monthDay", "")
        }
    }, [validation.values.useDefaultDates])

    const handleAddNew = () => {
        setShowAddForm(true)
        setReadOnly(false)
    }

    const handleCancel = () => {
        // Hide form in detail mode when cancel is clicked
        if (page === "detail") {
            setShowAddForm(false)
        }
        setReadOnly(true)
        validation.resetForm()
    }

    return (
        <>
            {variablesError && (
                <Alert color="danger" className="mb-3">
                    {variablesError}
                </Alert>
            )}

            <Card>
                <CardBody>
                    {/* Card Header with Add Button */}
                    {page === "detail" && !merchantVariables && !showAddForm && (
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Variables</h5>
                            <AccessControlWrapper
                                contextUri={url.CREATE_MERCHANT_VARIABLES}
                                actionType="post"
                            >
                                <CButton.Success
                                    icon="bx bx-plus"
                                    text="Add Variables"
                                    onClick={handleAddNew}
                                />
                            </AccessControlWrapper>
                        </div>
                    )}

                    {/* Show empty state when no data and no form */}
                    {page === "detail" && !merchantVariables && !showAddForm && (
                        <div className="text-center py-4">
                            <i className="bx bx-cog display-4 text-muted mb-3"></i>
                            <p className="text-muted">No variables configured yet</p>
                        </div>
                    )}

                    {/* Show edit button when data exists */}
                    {merchantVariables && readOnly && (
                        <div className="d-flex justify-content-end mb-3">
                            <AccessControlWrapper
                                contextUri={url.UPDATE_MERCHANT_VARIABLES}
                                actionType="post"
                            >
                                <CButton.Primary
                                    text="Edit Variables"
                                    onClick={() => setReadOnly(false)}
                                />
                            </AccessControlWrapper>
                        </div>
                    )}

                    {/* Show form in add mode or when showAddForm is true or when editing existing data */}
                    {(page === "add" || showAddForm || merchantVariables) && (
                        <div className="needs-validation">
                            {/* Second Row: Select inputs */}
                            <Row>
                                <Col md="6">
                                    <SelectInput
                                        name="reconciliationFrequency"
                                        label="Reconciliation Frequency"
                                        placeholder="Select Reconciliation Frequency..."
                                        options={reconciliationOptions}
                                        validation={validation}
                                        readOnly={readOnly}
                                        required={true}
                                        capitalizeReadOnly={true}
                                        onSelectionChange={(selectedOption) => {
                                            validation.setFieldValue("reconciliationFrequency", selectedOption?.value || "")
                                        }}
                                    />

                                    {/* Hide use default dates checkbox - Always use default dates */}
                                    {/* {(!readOnly || hasUseDefaultDatesValue()) && (
                                    <CheckboxInput
                                        name="useDefaultDates"
                                        validation={validation}
                                        readOnly={readOnly}
                                        disabled={isCheckboxDisabled}
                                        checkboxLabel="Use Default Dates"
                                        trueText="Default Dates Selected"
                                        falseText="Custom Dates"
                                    />
                                )} */}
                                </Col>

                                <Col md="6">
                                    {/* Second Row: Conditional Days Text - Show opted days for weekly, fortnightly */}
                                    {(validation.values.reconciliationFrequency === "monthly" || validation.values.reconciliationFrequency === "weekly" ||
                                        validation.values.reconciliationFrequency === "biweekly" ||
                                        validation.values.reconciliationFrequency === "fortnightly") && (
                                            <Row>
                                                <Col md="12">
                                                    <div style={{ color: "#495057", fontWeight: 500 }}>
                                                        <div className="mb-2">
                                                            <span className="text-muted">Opted Date(s) </span>
                                                        </div>
                                                        <span className="input-as-text">{getReconciliationDaysText()}</span>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    {/* Task 1: Hide start day select - Always use default dates */}
                                    {/* {(!readOnly || hasMonthDayValue()) && (
                                    <SelectInput
                                        name="monthDay"
                                        label={validation.values.reconciliationFrequency === "monthly" ? "Date" : "Start Date"}
                                        placeholder="Select Day..."
                                        options={monthDayOptions}
                                        validation={validation}
                                        readOnly={readOnly}
                                        disabled={isStartDayDisabled}
                                        required={validation.values.useDefaultDates !== true && validation.values.reconciliationFrequency !== "daily"}
                                        dropdownStyle={{
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            zIndex: 9999
                                        }}
                                        helperText={
                                            validation.values.reconciliationFrequency === "monthly" ? "Days 1-28 allowed for monthly" :
                                                validation.values.reconciliationFrequency === "fortnightly" ? "Days 1-14 allowed for fortnightly" :
                                                    validation.values.reconciliationFrequency === "weekly" ? "Days 1-7 allowed for weekly" :
                                                        validation.values.reconciliationFrequency === "biweekly" ? "Days 1-4 allowed for biweekly" :
                                                            ""
                                        }
                                    />
                                )} */}
                                </Col>
                            </Row>



                            {/* Third Row: Payment fields */}
                            <Row>
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="paymentProcessingFeePercent"
                                        placeholder="Enter Payment Processing Fee Percent"
                                        label="Payment Processing Fee Percent"
                                        helperText="Enter percentage (0-5)"
                                        validation={validation}
                                        readOnly={readOnly}
                                        required={true}
                                    />
                                </Col>
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="payoutProcessingFeePercent"
                                        placeholder="Enter Payout Processing Fee Percent"
                                        label="Payout Processing Fee Percent"
                                        helperText={(() => {
                                            const { min, max } = getPercentageRange(validation.values.reconciliationFrequency)
                                            return `Enter percentage (${min}-${max})`
                                        })()}
                                        validation={validation}
                                        readOnly={readOnly}
                                        required={true}
                                    />
                                </Col>
                            </Row>

                            {/* Fourth Row: Settlement Threshold */}
                            {/* <Row>
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name="settlementThreshold"
                                    placeholder="Enter Settlement Threshold"
                                    label="Settlement Threshold"
                                    helperText="Enter amount greater than zero"
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={false}
                                />
                            </Col>
                        </Row> */}

                            {!readOnly && (
                                <Row>
                                    <Col md="12" className="text-end">
                                        {(merchantVariables || page === "detail") && (
                                            <CButton.Secondary
                                                text="Cancel"
                                                className="me-2"
                                                onClick={handleCancel}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                        <ConfirmButton
                                            validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                            onConfirm={handleConfirmedSubmit}
                                            message={`Are you sure you want to ${merchantVariables ? "update" : "create"} merchant variables?`}
                                            color="primary"
                                            disabled={isSubmitting}
                                            contextUri={merchantVariables ? url.UPDATE_MERCHANT_VARIABLES : url.CREATE_MERCHANT_VARIABLES}
                                            actionType="post"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    {merchantVariables ? "Updating..." : "Creating..."}
                                                </>
                                            ) : (
                                                merchantVariables ? "Update Variable" : "Create Variable"
                                            )}
                                        </ConfirmButton>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>
        </>
    )
}